'use client'

import {StackedLayout} from "../tailwind/stacked-layout.tsx";
import {Navbar, NavbarDivider, NavbarItem, NavbarSection, NavbarSpacer} from "../tailwind/navbar.tsx";
import {Link} from "../tailwind/link.tsx";
import BrandLogo from "../brand-logo.tsx";
import {
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeading,
    SidebarItem,
    SidebarLabel,
    SidebarSection
} from "../tailwind/sidebar.tsx";
import {
    Dropdown,
    DropdownButton,
    DropdownDivider,
    DropdownItem,
    DropdownLabel,
    DropdownMenu
} from "../tailwind/dropdown.tsx";
import {
    AdjustmentsVerticalIcon,
    ArrowRightStartOnRectangleIcon,
    BookOpenIcon,
    ChevronUpIcon,
    CogIcon,
    CurrencyEuroIcon,
    HomeIcon,
    InformationCircleIcon,
    KeyIcon,
    ListBulletIcon,
    SparklesIcon,
    UserCircleIcon,
    UsersIcon,
    ChartBarIcon,
    CakeIcon,
    TruckIcon,
    UserMinusIcon,
    CreditCardIcon,
    MapPinIcon,
    PhoneIcon,
    EnvelopeIcon,
    BanknotesIcon,
} from '@heroicons/react/16/solid'
import {Outlet, useLocation, useNavigate} from "react-router-dom";
import {useMenuTranslation, useCommonTranslation} from "@/i18n/hooks";
import {useAuth} from "../../context/use-auth.tsx";
import {SyntheticEvent, useEffect, useState} from "react";
import {AppInfo, fetchAppInfo} from "@/services/app-info-services.ts";
import {Dialog, DialogActions, DialogBody, DialogTitle} from "../tailwind/dialog.tsx";
import {Button} from "../tailwind/button.tsx";
import log from "@/lib/logger";
import {getSystemEmailStatus} from "@/services/system-email-services.ts";
import {getSocialAccountEnabled} from "@/services/social-account-settings-services.ts";

function AccountDropdownMenu({anchor, onAboutClick}: {
    anchor: 'top start' | 'bottom end',
    onAboutClick: () => void,
}) {
    const {isLoggedIn, logout} = useAuth()
    const navigate = useNavigate()
    const {t} = useMenuTranslation()

    function doLogout() {
        if (isLoggedIn()) {
            logout()
        }

        navigate(`${import.meta.env.BASE_URL}`)
    }

    return (
        <DropdownMenu className="min-w-64" anchor={anchor}>
            <ChangePasswordMenuItem/>
            <ChangeEmailMenuItem/>

            <DropdownDivider/>
            <DropdownItem onClick={onAboutClick} data-testid="menu-about">
                <InformationCircleIcon/>
                <DropdownLabel>{t("menu.myAccount.about.dropdownLabel")}</DropdownLabel>
            </DropdownItem>

            <DropdownDivider/>
            <DropdownItem onClick={doLogout} data-testid="logout-button">
                <ArrowRightStartOnRectangleIcon/>
                <DropdownLabel aria-label={t("menu.myAccount.logout.ariaLabel")}>{t("menu.myAccount.logout.dropdownLabel")}</DropdownLabel>
            </DropdownItem>
        </DropdownMenu>
    )
}


function AboutDialog({open, onClose}: { open: boolean, onClose: () => void }) {
    const {t} = useCommonTranslation()
    const appInfo = useAppInfo()

    return (
        <Dialog open={open} onClose={onClose} size="sm">
            <DialogTitle>{t("appInfo.about.title")}</DialogTitle>
            <DialogBody>
                {appInfo ? (
                    <dl className="space-y-2 text-sm">
                        <div className="flex justify-between">
                            <dt className="text-zinc-500 dark:text-zinc-400">{t("appInfo.version")}</dt>
                            <dd className="font-medium text-zinc-900 dark:text-white" data-testid="app-version-display">{appInfo.version}</dd>
                        </div>
                        {appInfo.environment !== "PRODUCTION" && (
                            <div className="flex justify-between">
                                <dt className="text-zinc-500 dark:text-zinc-400">{t("appInfo.environment")}</dt>
                                <dd className="font-medium text-zinc-900 dark:text-white">{appInfo.environment}</dd>
                            </div>
                        )}
                    </dl>
                ) : (
                    <p className="text-sm text-zinc-500">{t("loading")}</p>
                )}
            </DialogBody>
            <DialogActions>
                <Button onClick={onClose}>{t("appInfo.about.close")}</Button>
            </DialogActions>
        </Dialog>
    )
}


function ChangePasswordMenuItem() {
    const {user, openChangePasswordDialog} = useAuth()
    const {t} = useMenuTranslation()

    if (!user) {
        return null
    }

    return (
        <>
            <DropdownItem 
                data-testid="menu-change-password"
                onClick={(e: SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openChangePasswordDialog()
            }}>
                <KeyIcon/>
                <DropdownLabel aria-label={t("menu.myAccount.changePassword.ariaLabel")}>
                    {t("menu.myAccount.changePassword.dropdownLabel")}
                </DropdownLabel>
            </DropdownItem>

        </>
    )
}


function ChangeEmailMenuItem() {
    const {user, openChangeEmailDialog} = useAuth()
    const {t} = useMenuTranslation()
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        getSystemEmailStatus()
            .then(status => setIsVisible(status.configured))
            .catch(() => setIsVisible(false))
    }, [])

    if (!user || !isVisible) {
        return null
    }

    return (
        <DropdownItem
            data-testid="menu-change-email"
            onClick={(e: SyntheticEvent) => {
                e.preventDefault();
                e.stopPropagation();
                openChangeEmailDialog()
            }}>
            <EnvelopeIcon/>
            <DropdownLabel aria-label={t("menu.myAccount.changeEmail.ariaLabel")}>
                {t("menu.myAccount.changeEmail.dropdownLabel")}
            </DropdownLabel>
        </DropdownItem>
    )
}


function useAppInfo() {
    const {token, isLoggedIn} = useAuth();
    const [appInfo, setAppInfo] = useState<AppInfo | null>(null);

    useEffect(() => {
        if (!isLoggedIn() || !token) return;

        fetchAppInfo(token)
            .then(setAppInfo)
            .catch((err) => log.warn("Failed to fetch app info", err));
    }, [token, isLoggedIn]);

    return appInfo;
}


export function Root() {

    const {pathname} = useLocation()
    const {user, isLoggedIn, isAdmin, token} = useAuth()
    const {t} = useMenuTranslation()
    const [aboutOpen, setAboutOpen] = useState(false)
    const [socialAccountEnabled, setSocialAccountEnabled] = useState(false)

    useEffect(() => {
        if (!token || !user) {
            setSocialAccountEnabled(false)
            return
        }
        getSocialAccountEnabled(token)
            .then(setSocialAccountEnabled)
            .catch(() => setSocialAccountEnabled(false))
    }, [token, user])

    return (
        <StackedLayout
            // for larger screen
            navbar={
                <Navbar>
                    <Link href="/" aria-label={t("menu.navbar.home.ariaLabel")}
                          className="flex shrink-0 items-center rounded-lg p-1">
                        <BrandLogo className="size-9"/>
                    </Link>

                    <NavbarSection className="max-lg:hidden">
                        <NavbarItem href="/" current={pathname === '/'}><HomeIcon/>{t("menu.navbar.home.label")}</NavbarItem>
                    </NavbarSection>

                    {/* User routes */}
                    {isLoggedIn() && (
                        <>

                            <NavbarDivider className="max-lg:hidden"/>

                            <NavbarSection className="max-lg:hidden"
                                           aria-label={t("menu.navbar.membersSection.ariaLabel")}>
                                <Dropdown>
                                    <DropdownButton as={NavbarItem}
                                                    data-testid="nav-members-dropdown">
                                        <UsersIcon/>{t("menu.navbar.members.label")} <ChevronUpIcon className="rotate-180"/>
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom start">
                                        <DropdownItem href={`${import.meta.env.BASE_URL}members`}
                                                      data-testid="nav-members">
                                            <UsersIcon/>
                                            <DropdownLabel>{t("menu.sidebar.members.overview.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}members/special-assignments`}
                                                      data-testid="nav-special-assignments-navbar">
                                            <SparklesIcon/>
                                            <DropdownLabel>{t("menu.sidebar.members.specialAssignments.label")}</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarSection>

                            <NavbarSection className="max-lg:hidden" aria-label={t("menu.navbar.orders.ariaLabel")}>
                                <Dropdown>
                                    <DropdownButton as={NavbarItem} data-testid="nav-orders-dropdown">
                                        <ListBulletIcon/>Aufträge <ChevronUpIcon className="rotate-180" />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom start">
                                        <DropdownItem href={`${import.meta.env.BASE_URL}orders/new`} data-testid="nav-orders-new">
                                            <ListBulletIcon/>
                                            <DropdownLabel>Neuer Auftrag</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}orders/open`} data-testid="nav-orders-open">
                                            <ListBulletIcon/>
                                            <DropdownLabel>Offene Aufträge</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}orders/completed`} data-testid="nav-orders-completed">
                                            <ListBulletIcon/>
                                            <DropdownLabel>Abgeschlossene Aufträge</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}orders/cancelled`} data-testid="nav-orders-cancelled">
                                            <ListBulletIcon/>
                                            <DropdownLabel>Stornierte Aufträge</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarSection>

                            <NavbarSection className="max-lg:hidden" aria-label={t("menu.sidebar.finance.heading")}>
                                <Dropdown>
                                    <DropdownButton as={NavbarItem} data-testid="nav-finance-dropdown">
                                        <CurrencyEuroIcon/>{t("menu.sidebar.finance.heading")} <ChevronUpIcon className="rotate-180" />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom start">
                                        <DropdownItem href={`${import.meta.env.BASE_URL}ledger`} data-testid="nav-ledger">
                                            <BookOpenIcon/>
                                            <DropdownLabel>{t("menu.sidebar.finance.ledger.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}finance/billing`} data-testid="nav-billing">
                                            <CurrencyEuroIcon/>
                                            <DropdownLabel>{t("menu.sidebar.finance.billing.label")}</DropdownLabel>
                                        </DropdownItem>
                                        {socialAccountEnabled && (
                                            <DropdownItem href={`${import.meta.env.BASE_URL}finance/social-account`} data-testid="nav-social-account">
                                                <BanknotesIcon/>
                                                <DropdownLabel>{t("menu.sidebar.finance.socialAccount.label")}</DropdownLabel>
                                            </DropdownItem>
                                        )}
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarSection>

                            <NavbarSection className="max-lg:hidden" aria-label="Auswertungen">
                                <Dropdown>
                                    <DropdownButton as={NavbarItem} data-testid="nav-reports-dropdown">
                                        <ChartBarIcon/>{t("menu.sidebar.reports.heading")} <ChevronUpIcon className="rotate-180" />
                                    </DropdownButton>
                                    <DropdownMenu anchor="bottom start">
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/birthdays`} data-testid="nav-birthdays">
                                            <CakeIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.birthdays.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/drivers`} data-testid="nav-drivers">
                                            <TruckIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.drivers.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/former-members`} data-testid="nav-former-members">
                                            <UserMinusIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.formerMembers.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/bank-data-validation`} data-testid="nav-bank-data-validation">
                                            <CreditCardIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.bankDataValidation.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/address-list`} data-testid="nav-address-list">
                                            <MapPinIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.addressList.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/phone-list`} data-testid="nav-phone-list">
                                            <PhoneIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.phoneList.label")}</DropdownLabel>
                                        </DropdownItem>
                                        <DropdownItem href={`${import.meta.env.BASE_URL}reports/statistics`} data-testid="nav-statistics">
                                            <ChartBarIcon/>
                                            <DropdownLabel>{t("menu.sidebar.reports.statistics.label")}</DropdownLabel>
                                        </DropdownItem>
                                    </DropdownMenu>
                                </Dropdown>
                            </NavbarSection>
                        </>
                    )}

                    {/* Admin routes */}
                    {isAdmin() && (
                        <>
                            <NavbarDivider/>
                            <NavbarSection className="max-lg:hidden"
                                           aria-label={t("menu.navbar.adminSection.ariaLabel")}>
                                <NavbarItem href={`${import.meta.env.BASE_URL}admin/users`}
                                            current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/users`)}
                                            data-testid="nav-users">
                                    <UsersIcon/>{t("menu.navbar.users.label")}
                                </NavbarItem>
                                <NavbarItem href={`${import.meta.env.BASE_URL}admin/services`}
                                            current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/services`)}
                                            data-testid="nav-services">
                                    <CogIcon/>{t("menu.navbar.services.label")}
                                </NavbarItem>
                                <NavbarItem href={`${import.meta.env.BASE_URL}admin/settings`}
                                            current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/settings`)}
                                            data-testid="nav-settings">
                                    <AdjustmentsVerticalIcon/>{t("menu.navbar.settings.label")}
                                </NavbarItem>
                            </NavbarSection>
                        </>

                    )}

                    <NavbarSpacer/>

                    <NavbarSection>
                        {isLoggedIn() && (

                            <Dropdown>
                                <DropdownButton as={NavbarItem} data-testid="account-dropdown">
                                    <UserCircleIcon/> <span data-testid="user-display">{user?.userName}</span>
                                </DropdownButton>
                                <AccountDropdownMenu anchor={"bottom end"} onAboutClick={() => setAboutOpen(true)}/>
                            </Dropdown>
                        )}

                        {!isLoggedIn() && (
                            <NavbarItem href={`${import.meta.env.BASE_URL}login`}
                                        current={pathname.startsWith(`${import.meta.env.BASE_URL}login`)}
                                        aria-label={t("menu.login.ariaLabel")}>{t("menu.login.text")}</NavbarItem>
                        )}
                    </NavbarSection>
                </Navbar>
            }
            // for mobile / smaller screens
            sidebar={
                <Sidebar>
                    <SidebarBody>
                        <SidebarSection>
                            <SidebarItem href={`${import.meta.env.BASE_URL}`}
                                         current={pathname === `${import.meta.env.BASE_URL}`}>
                                <HomeIcon/>
                                <SidebarLabel>{t("menu.navbar.home.label")}</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>

                        {isLoggedIn() && (
                            <>
                                <SidebarSection>
                                    <SidebarHeading>{t("menu.sidebar.members.heading")}</SidebarHeading>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}members`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}members`) && !pathname.startsWith(`${import.meta.env.BASE_URL}members/special-assignments`)}
                                                 data-testid="nav-members">
                                        <UsersIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.members.overview.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}members/special-assignments`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}members/special-assignments`)}
                                                 data-testid="nav-special-assignments">
                                        <SparklesIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.members.specialAssignments.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>
                                <SidebarSection>
                                    <SidebarHeading>{t("menu.sidebar.orders.heading")}</SidebarHeading>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}orders/new`}
                                                 current={pathname === `${import.meta.env.BASE_URL}orders/new`}
                                                 data-testid="nav-orders-new">
                                        <ListBulletIcon/>
                                        <SidebarLabel>
                                            Neuer Auftrag
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}orders/open`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}orders/open`)}
                                                 data-testid="nav-orders-open">
                                        <ListBulletIcon/>
                                        <SidebarLabel>
                                            Offene Aufträge
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}orders/completed`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}orders/completed`)}
                                                 data-testid="nav-orders-completed">
                                        <ListBulletIcon/>
                                        <SidebarLabel>
                                            Abgeschlossene Aufträge
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}orders/cancelled`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}orders/cancelled`)}
                                                 data-testid="nav-orders-cancelled">
                                        <ListBulletIcon/>
                                        <SidebarLabel>
                                            Stornierte Aufträge
                                        </SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>
                                <SidebarSection>
                                    <SidebarHeading>{t("menu.sidebar.finance.heading")}</SidebarHeading>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}ledger`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}ledger`)}
                                                 data-testid="nav-ledger">
                                        <BookOpenIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.finance.ledger.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}finance/billing`}
                                                 current={pathname.startsWith(`${import.meta.env.BASE_URL}finance/billing`)}
                                                 data-testid="nav-finance-billing">
                                        <CurrencyEuroIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.finance.billing.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    {socialAccountEnabled && (
                                        <SidebarItem href={`${import.meta.env.BASE_URL}finance/social-account`}
                                                     current={pathname.startsWith(`${import.meta.env.BASE_URL}finance/social-account`)}
                                                     data-testid="nav-finance-social-account">
                                            <BanknotesIcon/>
                                            <SidebarLabel>
                                                {t("menu.sidebar.finance.socialAccount.label")}
                                            </SidebarLabel>
                                        </SidebarItem>
                                    )}
                                </SidebarSection>
                                <SidebarSection>
                                    <SidebarHeading>{t("menu.sidebar.reports.heading")}</SidebarHeading>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/birthdays`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/birthdays`}
                                                 data-testid="nav-reports-birthdays">
                                        <CakeIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.birthdays.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/drivers`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/drivers`}
                                                 data-testid="nav-reports-drivers">
                                        <TruckIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.drivers.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/former-members`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/former-members`}
                                                 data-testid="nav-reports-former-members">
                                        <UserMinusIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.formerMembers.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/bank-data-validation`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/bank-data-validation`}
                                                 data-testid="nav-reports-bank-data-validation">
                                        <CreditCardIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.bankDataValidation.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/address-list`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/address-list`}
                                                 data-testid="nav-reports-address-list">
                                        <MapPinIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.addressList.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/phone-list`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/phone-list`}
                                                 data-testid="nav-reports-phone-list">
                                        <PhoneIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.phoneList.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                    <SidebarItem href={`${import.meta.env.BASE_URL}reports/statistics`}
                                                 current={pathname === `${import.meta.env.BASE_URL}reports/statistics`}
                                                 data-testid="nav-reports-statistics">
                                        <ChartBarIcon/>
                                        <SidebarLabel>
                                            {t("menu.sidebar.reports.statistics.label")}
                                        </SidebarLabel>
                                    </SidebarItem>
                                </SidebarSection>
                            </>
                        )}

                        {isAdmin() && (
                            <SidebarSection aria-label={t("menu.adminSection.ariaLabel")}>
                                <SidebarHeading>{t("menu.sidebar.admin.heading")}</SidebarHeading>
                                <SidebarItem
                                    href={`${import.meta.env.BASE_URL}admin/users`}
                                    current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/users`)}
                                    data-testid="nav-users"
                                >
                                    <UsersIcon/>
                                    <SidebarLabel>{t("menu.sidebar.admin.users.label")}</SidebarLabel>
                                </SidebarItem>
                                <SidebarItem
                                    href={`${import.meta.env.BASE_URL}admin/services`}
                                    current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/services`)}
                                    data-testid="nav-services"
                                >
                                    <CogIcon/>
                                    <SidebarLabel>{t("menu.sidebar.admin.services.label")}</SidebarLabel>
                                </SidebarItem>
                                <SidebarItem
                                    href={`${import.meta.env.BASE_URL}admin/settings`}
                                    current={pathname.startsWith(`${import.meta.env.BASE_URL}admin/settings`)}
                                    data-testid="nav-settings"
                                >
                                    <AdjustmentsVerticalIcon/>
                                    <SidebarLabel>{t("menu.sidebar.admin.settings.label")}</SidebarLabel>
                                </SidebarItem>
                            </SidebarSection>
                        )}

                    </SidebarBody>

                    <SidebarFooter className="max-lg:hidden">
                        <Dropdown>
                            <DropdownButton as={SidebarItem}>
                                <ChevronUpIcon/>
                            </DropdownButton>
                            <AccountDropdownMenu anchor="top start" onAboutClick={() => setAboutOpen(true)}/>
                        </Dropdown>
                    </SidebarFooter>
                </Sidebar>
            }
        >
            <Outlet/>
            <AboutDialog open={aboutOpen} onClose={() => setAboutOpen(false)}/>
        </StackedLayout>
    )
}
